/**
 * leagueBase.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared utilities used by every league component.
 * Import what you need — nothing here has side effects.
 */

/** Inject a <style> block once, replacing on HMR. */
export function injectCSS(id, css) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('style'); el.id = id; document.head.appendChild(el); }
  el.textContent = css;
}

/** Build a coloured W/D/L pill string. */
export function formPills(form = []) {
  return form.map(r => `<span class="lg-pill lg-pill-${r}">${r}</span>`).join('');
}

/** GD with sign and colour class. */
export function gdHtml(n) {
  if (n > 0) return `<span class="lg-pos">+${n}</span>`;
  if (n < 0) return `<span class="lg-neg">${n}</span>`;
  return '0';
}

/** Format 'YYYY-MM-DD' → '12 Apr' */
export function fmtDate(s) {
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

/**
 * SHARED CSS — injected by each component under its own id.
 * Every component must call  injectCSS('lg-base-styles', BASE_CSS)  once.
 */
export const BASE_CSS = `
  /* ── Shared base ────────────────────────────────── */
  .lg-wrap { font-family: var(--font-sans, system-ui, sans-serif); color: var(--text-color, #111); }

  .lg-hero { display:flex; align-items:center; gap:10px; padding:14px 0 12px;
             border-bottom:1px solid var(--border-color,#eee); flex-wrap:wrap; }
  .lg-logo { width:40px; height:40px; border-radius:10px; display:flex;
             align-items:center; justify-content:center; flex-shrink:0; font-size:18px; }
  .lg-hero-text h2 { font-size:16px; font-weight:700; margin:0; }
  .lg-hero-text p  { font-size:11px; color:var(--text-secondary,#666); margin:2px 0 0; }
  .lg-badge { margin-left:auto; font-size:11px; font-weight:600;
              padding:3px 10px; border-radius:20px; white-space:nowrap; }

  /* ── Tabs ───────────────────────────────────────── */
  .lg-tabs { display:flex; border-bottom:1px solid var(--border-color,#eee);
             overflow-x:auto; scrollbar-width:none; }
  .lg-tabs::-webkit-scrollbar { display:none; }
  .lg-tab { padding:9px 15px; font-size:12px; font-weight:400; cursor:pointer;
            color:var(--text-secondary,#666); border-bottom:2px solid transparent;
            white-space:nowrap; min-height:44px; display:flex; align-items:center;
            transition:color .15s; flex-shrink:0; }
  .lg-tab.active { color:var(--text-color,#111); font-weight:600; }
  .lg-tab:hover:not(.active) { color:var(--text-color,#111); }

  .lg-panel { display:none; }
  .lg-panel.active { display:block; }

  /* ── Layout ─────────────────────────────────────── */
  .lg-layout { display:grid; grid-template-columns:1fr 220px; }
  .lg-sidebar { border-right:1px solid var(--border-color,#eee); }
  .lg-sidebar-sec { padding:11px; border-bottom:1px solid var(--border-color,#eee); }
  .lg-sec-lbl { font-size:10px; font-weight:600; letter-spacing:.6px;
                text-transform:uppercase; color:var(--text-secondary,#888); margin-bottom:8px; }
  .lg-main { padding:12px 13px; }

  /* ── Standings table ────────────────────────────── */
  .lg-table { width:100%; border-collapse:collapse; font-size:12px; margin-top:10px; }
  .lg-table th { font-size:10px; font-weight:500; color:var(--text-secondary,#888);
                 text-align:center; padding:4px 2px 6px;
                 border-bottom:1px solid var(--border-color,#eee); }
  .lg-table th.l { text-align:left; }
  .lg-table td { padding:8px 2px; border-bottom:1px solid var(--border-color,#f0f0f0); text-align:center; }
  .lg-table td.l { text-align:left; }
  .lg-table tr:hover td { background:var(--hover-bg,#fafafa); }
  .lg-zone-a td:first-child { border-left:3px solid #27ae60; padding-left:4px; }
  .lg-zone-b td:first-child { border-left:3px solid #3498db; padding-left:4px; }
  .lg-zone-r td:first-child { border-left:3px solid #e74c3c; padding-left:4px; }
  .lg-zone-n td:first-child { border-left:3px solid transparent; padding-left:4px; }
  .lg-team-cell { display:flex; align-items:center; gap:5px; }
  .lg-dot  { width:8px; height:8px; border-radius:2px; flex-shrink:0; }
  .lg-name { font-weight:400; white-space:nowrap; overflow:hidden;
             text-overflow:ellipsis; max-width:155px; }
  .lg-pts  { font-weight:700; }
  .lg-pos  { color:#27ae60; font-weight:600; }
  .lg-neg  { color:#e74c3c; font-weight:600; }

  /* ── Form pills ─────────────────────────────────── */
  .lg-form  { display:flex; gap:2px; justify-content:center; }
  .lg-pill  { width:14px; height:14px; border-radius:3px; font-size:8px;
              font-weight:700; display:flex; align-items:center; justify-content:center; }
  .lg-pill-W { background:#27ae60; color:#fff; }
  .lg-pill-D { background:#888; color:#fff; }
  .lg-pill-L { background:#e74c3c; color:#fff; }

  /* ── Scorer rows ────────────────────────────────── */
  .lg-scorer { display:flex; align-items:center; gap:7px; padding:6px 0; }
  .lg-scorer-rank { font-size:10px; color:var(--text-secondary,#888); min-width:13px; }
  .lg-av  { width:25px; height:25px; border-radius:50%; background:var(--hover-bg,#f0f0f0);
            border:1px solid var(--border-color,#eee); display:flex; align-items:center;
            justify-content:center; font-size:9px; font-weight:600; flex-shrink:0; }
  .lg-scorer-info { flex:1; min-width:0; }
  .lg-scorer-name { font-size:11px; font-weight:600; white-space:nowrap;
                    overflow:hidden; text-overflow:ellipsis; }
  .lg-scorer-club { font-size:10px; color:var(--text-secondary,#888); }
  .lg-scorer-g    { font-size:12px; font-weight:700; flex-shrink:0; }

  /* ── Fixture rows ───────────────────────────────── */
  .lg-fix { display:flex; align-items:center; gap:5px; padding:7px 0;
            border-bottom:1px solid var(--border-color,#eee); font-size:11px; }
  .lg-fix:last-child { border-bottom:none; }
  .lg-fix-date  { font-size:10px; color:var(--text-secondary,#888); min-width:30px;
                  text-align:center; line-height:1.3; flex-shrink:0; }
  .lg-fix-teams { flex:1; min-width:0; }
  .lg-fix-team  { display:flex; align-items:center; gap:4px; margin:1px 0; }
  .lg-fix-name  { font-size:11px; white-space:nowrap; overflow:hidden;
                  text-overflow:ellipsis; max-width:96px; }
  .lg-fix-score { font-size:12px; font-weight:700; min-width:28px; text-align:right; flex-shrink:0; }
  .lg-fix-score.upcoming { color:var(--text-secondary,#888); font-weight:400; font-size:11px; }
  .lg-fix-score.live { color:#d32f2f; }

  /* ── About / meta ───────────────────────────────── */
  .lg-about h3 { font-size:14px; font-weight:700; margin:0 0 7px; }
  .lg-about p  { font-size:13px; color:var(--text-secondary,#666); line-height:1.65; margin-bottom:10px; }
  .lg-meta  { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:10px; }
  .lg-meta-item { background:var(--hover-bg,#f8f8f8); border-radius:8px; padding:9px 11px;
                  border:1px solid var(--border-color,#eee); }
  .lg-meta-lbl { font-size:10px; text-transform:uppercase; letter-spacing:.5px;
                 color:var(--text-secondary,#888); margin-bottom:3px; }
  .lg-meta-val { font-size:12px; font-weight:700; }

  /* ── Legend ─────────────────────────────────────── */
  .lg-legend { display:flex; flex-wrap:wrap; gap:9px; margin-top:9px; }
  .lg-leg-item { display:flex; align-items:center; gap:4px; font-size:11px;
                 color:var(--text-secondary,#888); }
  .lg-leg-dot  { width:8px; height:8px; border-radius:2px; }

  /* ── Responsive ─────────────────────────────────── */
  @media (max-width:768px) {
    .lg-layout { grid-template-columns:1fr; }
    .lg-main    { order:1; padding:11px 8px; }
    .lg-sidebar { order:2; border-right:none; border-top:1px solid var(--border-color,#eee); }
    .lg-name  { max-width:95px; }
    .lg-col-hide { display:none !important; }
    .lg-meta  { grid-template-columns:1fr; }
    .lg-fix { flex-direction:column; align-items:center; padding:10px 0; gap:3px; text-align:center; }
    .lg-fix-date { min-width:auto; }
    .lg-fix-teams { width:100%; display:flex; flex-direction:column; align-items:center; gap:3px; }
    .lg-fix-team  { justify-content:center; }
    .lg-fix-score { min-width:auto; text-align:center; }
    .lg-scorer    { padding:9px 0; }
  }
  @media (max-width:400px) {
    .lg-name { max-width:75px; }
    .lg-tab  { padding:9px 10px; font-size:11px; }
  }
`;
